import { version as msalBrowserVersion } from '@azure/msal-browser';

/**
 * Clears MSAL localStorage cache left over from an older major version.
 *
 * MSAL persists its token cache in localStorage. Across a major @azure/msal-browser
 * upgrade (e.g. v3 -> v5) the cache schema changes, and stale older-version entries make
 * the new client hang during `instance.initialize()` (the app gets stuck on the loading
 * screen). Previously the only workaround was manually clearing localStorage.
 *
 * Call this from main.ts BEFORE the app is bootstrapped, so the MSAL instance is built
 * against a clean cache. When the stored major differs from the running MSAL major, every
 * `msal.*` key is removed and the marker is updated. App-owned keys (token, url, ...) do
 * not start with `msal.` and are left untouched. After the purge MsalGuard re-authenticates,
 * normally silently via the existing AAD session (SSO) — no infinite spinner, no page reload.
 */
const CACHE_VERSION_KEY = 'bbj.msalCacheVersion';

export function ensureMsalCacheVersion(): void {
  try {
    const currentMajor = msalBrowserVersion.split('.')[0];
    const storedMajor = localStorage.getItem(CACHE_VERSION_KEY);

    if (storedMajor === currentMajor) {
      return;
    }

    Object.keys(localStorage)
      .filter((key) => key.startsWith('msal.'))
      .forEach((key) => localStorage.removeItem(key));

    localStorage.setItem(CACHE_VERSION_KEY, currentMajor);
  } catch {
    // localStorage unavailable (private mode / disabled) — nothing to purge.
  }
}
